var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, ButtonBase, CircularProgress, Typography, useTheme, } from '@mui/material';
import { useDinPlayerContext } from 'hx-din-test-2';
import { useNavigator } from 'hx-navigator';
import { Fade, Keypad, SideImageLayout, TripletInput, useKeypad } from 'hx-ui';
import { useCallback, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { useSetProgress } from 'hx-progress';
import { IoMdBackspace } from 'react-icons/io';
import { dinTestActions, selectActiveTestStep } from '@/store';
import { useStyledModal } from '@/hooks/use-styled-modal.hook';
import { keys } from '@/components/din-test/constants/keys.constant';
import { HelpModalContent, IdleModalContent, } from '@/components/din-test/components/modals/modals.component';
import { InfoMessage } from '@/components/din-test/notes.component';
import { NavigationButton, NavigationButtonsContainer, } from '@/components/navigation-button.component';
import { KeypadButton } from '@/components/din-test/components/keypad-button.component';
import { Footer } from '@/components/footer.component';
import bgGraphic from '@/assets/images/bg-graphic.svg';
export function DinTestPage() {
    var theme = useTheme();
    var id = useParams().id;
    var dispatch = useDispatch();
    var _a = useDinPlayerContext(), playTriplet = _a.playTriplet, initialize = _a.initialize, state = _a.state, stop = _a.stop;
    var _b = useState(''), error = _b[0], setError = _b[1];
    var _c = useKeypad(function () { return setError(''); }), value = _c.value, handleChange = _c.handleChange, handleBackspace = _c.handleBackspace, isShaking = _c.isShaking, shake = _c.shake, setValue = _c.setValue;
    var next = useNavigator({
        phaseLength: 23,
        onComplete: function () {
            dispatch(dinTestActions.processResult());
            dispatch(dinTestActions.sendData());
        },
    }).next;
    var activeStep = useSelector(selectActiveTestStep((id && +id - 1) || 0));
    useSetProgress(1, (id && +id) || 0);
    useMount(function () {
        if (!state.initialized)
            initialize();
        dispatch(dinTestActions.initializeTest());
    });
    useEffect(function () {
        dispatch(dinTestActions.setSnr(id));
    }, [id]);
    var playChallenge = useCallback(function () {
        if ((activeStep === null || activeStep === void 0 ? void 0 : activeStep.snr) === undefined)
            return;
        playTriplet(activeStep.challenge, {
            snr: activeStep.snr,
        });
    }, [activeStep]);
    useEffect(function () {
        playChallenge();
    }, [activeStep]);
    var handleSubmit = useCallback(function () {
        if (value.length < 3) {
            setError('Saisissez au moins 3 chiffres, puis cliquez sur Envoyer.');
            return shake();
        }
        var response = value;
        dispatch(dinTestActions.submitResponse({ response: response, id: id }));
        setValue([]);
        stop();
        next();
    }, [value, id]);
    var _d = useStyledModal(_jsx(IdleModalContent, {})), idleModal = _d[0], toggleIdleModal = _d[1], idleModalOpen = _d[2];
    var _e = useStyledModal(_jsx(HelpModalContent, {})), helpModal = _e[0], toggleHelpModal = _e[1], helpModalOpen = _e[2];
    useIdleTimer({
        onIdle: function () {
            if (idleModalOpen || helpModalOpen)
                return;
            toggleIdleModal();
        },
        timeout: 1000 * 15,
    });
    if (!state.initialized)
        return (_jsx("div", __assign({ className: "grid place-items-center min-h-[31.25rem]" }, { children: _jsx(CircularProgress, {}) })));
    return (_jsxs("div", __assign({ className: "relative" }, { children: [_jsx("img", { src: bgGraphic, alt: "", "aria-hidden": true, className: "pointer-events-none absolute bottom-0 right-0 z-0 hidden select-none md:block w-1/5" }), _jsx(SideImageLayout, { className: 'relative z-10 overflow-y-auto', content: _jsx(Fade, __assign({ className: "grid gap-4 place-items-center pt-8" }, { children: state.error ? (_jsx(ErrorMessage, { action: playChallenge })) : (_jsxs(_Fragment, { children: [_jsxs(Typography, __assign({ variant: "h2", className: "text-3xl md:text-4xl md:font-extrabold -mt-4" }, { children: ["\u00C9tape", ' ', _jsxs("span", __assign({ className: "font-normal text-primary" }, { children: [id, " sur 23"] }))] })), _jsxs("div", __assign({ className: "flex flex-col gap-4" }, { children: [_jsxs("div", __assign({ className: "relative mx-auto w-fit" }, { children: [_jsx(TripletInput, { className: "grid-cols-[repeat(3,minmax(0,60px))]", value: value, isShaking: isShaking, keepInactiveBorder: true, digitStyle: {
                                                    fontSize: '1.875rem',
                                                    fontFamily: 'Raleway, Inter, sans-serif',
                                                    fontWeight: 800,
                                                    minHeight: '2em',
                                                    borderBottomWidth: '3px',
                                                    paddingLeft: '10px',
                                                    paddingRight: '10px',
                                                    borderBottomColor: theme.palette.primary.main,
                                                    '&.has-value': {
                                                        borderBottomColor: theme.palette.primary.main,
                                                    },
                                                } }), _jsx(ButtonBase, __assign({ "aria-label": "Effacer le dernier chiffre", onClick: handleBackspace, className: "absolute left-full ml-3 top-1/2 -translate-y-1/2 p-0" }, { children: _jsx(IoMdBackspace, { size: 34, color: theme.palette.secondary.main }) }))] })), _jsx(Keypad, { value: value, onChange: handleChange, onBackspace: handleBackspace, KeypadButton: KeypadButton, onSubmit: handleSubmit, keys: keys })] })), _jsx(InfoMessage, { id: id, error: error }), idleModal, helpModal] })) })), nav: _jsxs(NavigationButtonsContainer, __assign({ className: "flex-row" }, { children: [_jsx(NavigationButton, { variant: "outlined", label: "Aide", onClick: toggleHelpModal, className: "py-2 text-xl" }), _jsx(NavigationButton, { label: "Suivant", onClick: handleSubmit, variant: "outlined", className: "py-2 text-xl" })] })), footer: _jsx(Footer, { className: "pt-2" }) })] })));
}
var ErrorMessage = function (_a) {
    var action = _a.action;
    return (_jsxs("div", __assign({ className: "grid gap-4 place-items-center" }, { children: [_jsx(Typography, { children: "Oups! Une erreur s'est produite lors de la lecture de l'audio." }), _jsx(Button, __assign({ onClick: action }, { children: "R\u00E9essayer" }))] })));
};
